const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL;

export class AuthExpiredError extends Error {
  constructor(message = '登入已過期，請重新登入') {
    super(message);
    this.name = 'AuthExpiredError';
  }
}

let onAuthExpiredHandler: (() => void) | null = null;
export function setOnAuthExpired(handler: (() => void) | null) {
  onAuthExpiredHandler = handler;
}

// ================================================================= //
//                            共用 API Client 主體                       //
// ================================================================= //

const apiClient = async (endpoint: string, options: RequestInit = {}) => {
  let token: string | null = null;

  if (typeof window !== 'undefined') {
    token = localStorage.getItem('accessToken');
  }

  const headers = {
    'Content-Type': 'application/json',
    'X-BigSmile-Client': 'official',
    ...options.headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if ((response.status === 401) &&
      !endpoint.includes('/auth/login') &&
      !endpoint.includes('/auth/access-token')) {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('accessToken');
      }
      if (onAuthExpiredHandler) {
        onAuthExpiredHandler();
      } else if (typeof window !== 'undefined') {
        Promise.resolve().then(() => window.location.assign('/login'));
      }
      throw new AuthExpiredError();
    }
    const errorData = await response.json().catch(() => ({ detail: '發生未知錯誤' }));
    throw new Error(errorData.detail || 'API 請求失敗');
  }

  if (response.status === 204) {
    return;
  }

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.indexOf("application/json") !== -1) {
    return response.json();
  }
  return;
};

// ================================================================= //
//                            TypeScript 型別定義                      //
// ================================================================= //

export interface Company {
  id: string;
  name: string;
  contact_person?: string;
  contact_email?: string;
  status: 'active' | 'inactive';
  frontend_url: string;
  homepage_content?: string;
  homepage_mode?: 'editor' | 'html' | 'redirect';
  redirect_url?: string;
  created_at: string;
}

export type CompanyCreate = Omit<Company, 'id' | 'created_at'>;
export type CompanyUpdate = Partial<CompanyCreate>;

export interface School {
  id: string;
  title: string;
  company_id?: string;
  active_payment_gateway?: string;
  payment_settings?: PaymentSettings;
  manual_transfer_config?: {
    bank_code: string;
    account_number: string;
    account_name: string;
    instructions: string;
  };
}

export type SchoolCreate = {
  title: string;
  account: string;
  password?: string;
  status: 'draft' | 'published';
  company_id: string;
};

export type SchoolUpdate = Partial<SchoolCreate>;

export interface UploadUrlResponse {
  url: string;
  asset_path: string;
}

export interface PaymentConfig {
  platform_installment_fees: {
    fees: { [key: string]: number };
  };
  payout: {
    fee: number;
    min_amount: number;
  };
}

export interface PaymentConfigUpdate {
  platform_installment_fees?: { [key: string]: number };
  payout_fee?: number;
  min_payout_amount?: number;
}

export interface ECPaySettings {
  merchant_id?: string;
  hash_key_secret_name?: string;
  hash_iv_secret_name?: string;
}

export interface PayUniSettings {
  username?: string;
  password_secret_name?: string;
  api_link_id?: string;
  hash_base_secret_name?: string;
  api_base_url?: string;
}

export interface NewebPaySettings {
  merchant_id?: string;
  hash_key_secret_name?: string;
  hash_iv_secret_name?: string;
  api_base_url?: string;
}

export interface PaymentSettings {
  active_payment_gateway?: 'ecpay' | 'payuni' | 'newebpay';
  ecpay?: ECPaySettings;
  payuni?: PayUniSettings;
  newebpay?: NewebPaySettings;
  platform_fees?: {
    fees?: { [key: string]: number };
  };
}

export interface PayoutRequest {
  id: string;
  school_id: string;
  company_name: string;
  school_name: string;
  wallet_balance: number;
  amount: number;
  status: 'pending' | 'completed' | 'rejected';
  requested_at: string; // or Date
  completed_at?: string; // or Date
  bank_code: string;
  bank_account: string;
  owner_name: string;
  rejection_reason?: string;
}


// ================================================================= //
//                            公司 (Company) API                       //
// ================================================================= //

export const getCompanies = (): Promise<Company[]> => {
  return apiClient('/api/v1/companies');
};

export const getCompany = (id: string): Promise<Company> => {
  return apiClient(`/api/v1/companies/${id}`);
};

export const createCompany = (data: CompanyCreate): Promise<Company> => {
  return apiClient('/api/v1/companies', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateCompany = (id: string, data: CompanyUpdate): Promise<Company> => {
  return apiClient(`/api/v1/companies/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deleteCompany = (id: string): Promise<void> => {
  return apiClient(`/api/v1/companies/${id}`, {
    method: 'DELETE',
  });
};

export const getCompanyHomepage = (companyId: string): Promise<{ homepage_content: string; homepage_mode: string }> => {
  return apiClient(`/api/v1/companies/${companyId}/homepage`);
};

export const updateCompanyHomepage = (companyId: string, content: string, mode: string = 'editor', redirectUrl?: string): Promise<{ homepage_content: string; homepage_mode: string; redirect_url?: string }> => {
  return apiClient(`/api/v1/companies/${companyId}/homepage`, {
    method: 'PUT',
    body: JSON.stringify({ homepage_content: content, homepage_mode: mode, redirect_url: redirectUrl }),
  });
};

export const getCompanyFooter = (companyId: string): Promise<{ footer_content_blocks: string }> => {
  return apiClient(`/api/v1/companies/${companyId}/footer`);
};

export const updateCompanyFooter = (companyId: string, content: string): Promise<{ footer_content_blocks: string }> => {
  return apiClient(`/api/v1/companies/${companyId}/footer`, {
    method: 'PUT',
    body: JSON.stringify({ footer_content_blocks: content }),
  });
};


// ================================================================= //
//                            學院 (School) API                        //
// ================================================================= //

export const createSchool = (data: SchoolCreate): Promise<School> => {
  return apiClient('/api/v1/users/schools/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateSchool = (id: string, data: SchoolUpdate): Promise<School> => {
  return apiClient(`/api/v1/users/schools/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const getSchool = (id: string): Promise<School> => {
  return apiClient(`/api/v1/school/${id}`);
}


// ================================================================= //
//                            檔案上傳 (Assets) API                     //
// ================================================================= //

export const generateAdminUploadUrl = (
  fileName: string,
  contentType: string,
  companyId: string
): Promise<UploadUrlResponse> => {
  return apiClient('/api/v1/assets/generate-admin-upload-url', {
    method: 'POST',
    body: JSON.stringify({
      file_name: fileName,
      content_type: contentType,
      company_id: companyId,
    }),
  });
};


// ================================================================= //
//                         平台系統 (System) API                       //
// ================================================================= //

export const getPaymentConfig = (): Promise<PaymentConfig> => {
  return apiClient('/api/v1/system/payment-config');
};

export const updatePaymentConfig = (data: PaymentConfigUpdate): Promise<PaymentConfig> => {
  return apiClient('/api/v1/system/payment-config', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const updateSchoolPaymentSettings = (schoolId: string, settingsData: PaymentSettings): Promise<School> => {
  return apiClient(`/api/v1/system/schools/${schoolId}/payment-settings`, {
    method: 'PUT',
    body: JSON.stringify(settingsData),
  });
};

// ================================================================= //
//                      提款管理 (Payout) API                         //
// ================================================================= //

export const getPayoutRequests = (status?: string): Promise<PayoutRequest[]> => {
  const endpoint = status ? `/api/v1/system/payout-requests?status=${status}` : '/api/v1/system/payout-requests';
  return apiClient(endpoint);
};

export const completePayoutRequest = (requestId: string): Promise<PayoutRequest> => {
  return apiClient(`/api/v1/system/payout-requests/${requestId}/complete`, {
    method: 'POST',
  });
};

export const rejectPayoutRequest = (requestId: string, reason: string): Promise<PayoutRequest> => {
  return apiClient(`/api/v1/system/payout-requests/${requestId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ rejection_reason: reason }),
  });
};

// ================================================================= //
//                      交易管理 (Transaction) API                     //
// ================================================================= //

export interface Transaction {
  id: string;
  amount: number;
  description: string;
  type: string;
  created_at: string;
  school_id: string;
}

export interface TransactionCreate {
  amount: number;
  description: string;
  type?: string;
  created_at?: string;
}

export interface TransactionUpdate {
  amount?: number;
  description?: string;
  type?: string;
  created_at?: string;
}

export interface TransactionListResponse {
  data: Transaction[];
  total: number;
}

export interface WalletStats {
  label: string;
  value: string;
  amount: number;
}

export const getSchoolTransactions = (
  schoolId: string,
  page = 1,
  limit = 20,
  month?: string
): Promise<TransactionListResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });
  if (month && month !== 'all') {
    params.append('month', month);
  }
  return apiClient(`/api/v1/system/schools/${schoolId}/transactions?${params.toString()}`);
};

export const getSchoolWalletStats = (schoolId: string): Promise<WalletStats[]> => {
  return apiClient(`/api/v1/system/schools/${schoolId}/wallet/stats`);
};

export const createTransaction = (schoolId: string, data: TransactionCreate): Promise<Transaction> => {
  return apiClient(`/api/v1/system/schools/${schoolId}/transactions`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateTransaction = (transactionId: string, data: TransactionUpdate): Promise<Transaction> => {
  return apiClient(`/api/v1/system/transactions/${transactionId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deleteTransaction = (transactionId: string): Promise<void> => {
  return apiClient(`/api/v1/system/transactions/${transactionId}`, {
    method: 'DELETE',
  });
};


export default apiClient;

// ================================================================= //
//                      平台服務費 (Platform Fee) API                   //
// ================================================================= //

export interface PlatformFee {
  id: string;
  amount: number;
  description: string;
  type: string;
  created_at: string;
  school_id?: string;
  is_manual?: boolean;
}

export interface PlatformFeeCreate {
  amount: number;
  description: string;
  type?: string;
  created_at?: string;
}

export interface PlatformFeeUpdate {
  amount?: number;
  description?: string;
  type?: string;
  created_at?: string;
}

export interface PlatformFeeListResponse {
  data: PlatformFee[];
  total: number;
}

export interface PlatformFeeStats {
  label: string;
  value: string;
  amount: number;
}

export const getSchoolPlatformFees = (
  schoolId: string,
  page = 1,
  limit = 20,
  month?: string
): PlatformFeeListResponse | Promise<PlatformFeeListResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });
  if (month && month !== 'all') {
    params.append('month', month);
  }
  return apiClient(`/api/v1/system/schools/${schoolId}/platform-fees?${params.toString()}`);
};

export const getSchoolPlatformFeeStats = (schoolId: string): Promise<PlatformFeeStats[]> => {
  return apiClient(`/api/v1/system/schools/${schoolId}/platform-fees/stats`);
};

export const createPlatformFee = (schoolId: string, data: PlatformFeeCreate): Promise<PlatformFee> => {
  return apiClient(`/api/v1/system/schools/${schoolId}/platform-fees`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updatePlatformFee = (feeId: string, data: PlatformFeeUpdate): Promise<PlatformFee> => {
  return apiClient(`/api/v1/system/platform-fees/${feeId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deletePlatformFee = (feeId: string): Promise<void> => {
  return apiClient(`/api/v1/system/platform-fees/${feeId}`, {
    method: 'DELETE',
  });
};

export const getGlobalPlatformFeeStats = (): Promise<{ total_balance: number; total_withdrawn: number }> => {
  return apiClient('/api/v1/system/platform-fees/global-stats');
};

// ================================================================= //
//                      GCP Cost (GCP 運算費用) API                   //
// ================================================================= //

// 誠實版：金額一律可為 null（未設定該月 GCP 真實總額時）。分攤依「照片上傳量比例」。
export interface GcpCostItemBreakdown {
  firestore: number | null;
  cloud_run: number | null;
  cloud_build: number | null;
  network: number | null;
  storage: number | null;
  notifications: number | null;
}

export interface GcpCostRecord {
  id: string;
  school_id: string;
  date: string;
  month: string;
  items: GcpCostItemBreakdown;
  photo_count: number;
  request_count?: number | null; // usage 基準：當日該校請求數
  total: number | null;
}

export interface GcpCostStats {
  label: string;
  value: string;
  amount: number | null;
  configured: boolean;
  proportion: number | null;
  photo_count: number;
  // usage=依請求數分攤；photo=歷史 fallback 依照片；null=舊後端/無資料（視為 photo）
  basis?: 'usage' | 'photo' | null;
}

export interface GcpCostListResponse {
  data: GcpCostRecord[];
  total: number;
  month: string;
  configured: boolean;
  month_total_usd: number | null;
  // 分攤基準比例：basis=usage 時為「請求數占比」，basis=photo 時為「照片占比」
  school_proportion: number | null;
  school_photo_count: number;
  school_request_count?: number | null; // usage 基準：該校該月請求數
  method: string;
  is_estimate: boolean;
  basis?: 'usage' | 'photo' | null;
}

export interface GcpMonthlyTotalSetting {
  month: string;
  configured: boolean;
  total_usd: number | null;
  by_sku: GcpCostItemBreakdown | null;
  currency: string;
  note: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

export interface GcpMonthlyTotalUpdate {
  total_usd: number;
  by_sku?: GcpCostItemBreakdown | null;
  note?: string | null;
}

export interface GcpReconSchoolRow {
  school_id: string;
  name: string;
  photo_count: number;
  request_count?: number | null; // usage 基準：該校該月請求數
  proportion: number;
  amount: number | null;
}

export interface GcpReconciliationResponse {
  month: string;
  configured: boolean;
  total_usd: number | null;
  method: string;
  is_estimate: boolean;
  total_photo_count: number;
  total_request_count?: number | null; // usage 基準：全體系該月請求數
  schools: GcpReconSchoolRow[];
  unallocated: GcpReconSchoolRow;
  allocated_sum: number | null;
  generated_at: string;
  basis?: 'usage' | 'photo' | null;
}

// 熱點雷達：依請求數統計各路由 hits
export interface GcpHotRoute {
  route_key: string; // "{METHOD} {route_template}"
  count: number;
}

export interface GcpHotRoutesResponse {
  month: string;
  scope: 'all' | 'school';
  school_id: string | null;
  total_requests: number;
  routes: GcpHotRoute[]; // 已依 count 降序
  generated_at: string;
}

export const getSchoolGcpCostStats = (schoolId: string): Promise<GcpCostStats[]> => {
  return apiClient(`/api/v1/system/schools/${schoolId}/gcp-costs/stats`);
};

export const getSchoolGcpCosts = (schoolId: string, month: string): Promise<GcpCostListResponse> => {
  return apiClient(`/api/v1/system/schools/${schoolId}/gcp-costs?month=${month}`);
};

export const getGcpTotalSetting = (month: string): Promise<GcpMonthlyTotalSetting> => {
  return apiClient(`/api/v1/system/gcp-costs/settings/${month}`);
};

export const upsertGcpTotalSetting = (month: string, payload: GcpMonthlyTotalUpdate): Promise<GcpMonthlyTotalSetting> => {
  return apiClient(`/api/v1/system/gcp-costs/settings/${month}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const getGcpReconciliation = (month: string): Promise<GcpReconciliationResponse> => {
  return apiClient(`/api/v1/system/gcp-costs/reconciliation?month=${month}`);
};

// 熱點雷達（admin only）。scope=school 時預設帶入 schoolId 為 school_id。
export const getGcpHotRoutes = (
  schoolId: string,
  opts: { month: string; scope: 'all' | 'school'; school_id?: string; top_n?: number },
): Promise<GcpHotRoutesResponse> => {
  const params = new URLSearchParams();
  params.set('month', opts.month);
  params.set('scope', opts.scope);
  const effectiveSchoolId = opts.scope === 'school' ? (opts.school_id ?? schoolId) : opts.school_id;
  if (effectiveSchoolId) params.set('school_id', effectiveSchoolId);
  if (opts.top_n != null) params.set('top_n', String(opts.top_n));
  return apiClient(`/api/v1/system/gcp-costs/hot-routes?${params.toString()}`);
};

